/**
 * Node Action Builders
 *
 * Generates customizable action lists for nodes based on entity state.
 * Supports conditional logic (isLaunchBlock, permissions, status, etc).
 */

import React from 'react';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import ContentCopy from '@mui/icons-material/ContentCopy';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import CollectionsBookmark from '@mui/icons-material/CollectionsBookmark';
import PlayArrow from '@mui/icons-material/PlayArrow';
import OpenInNew from '@mui/icons-material/OpenInNew';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Upgrade from '@mui/icons-material/Upgrade';

import { NodeAction } from '../../types/node-actions';
import { StrategyBlock } from '@segmently/schema-models/strategy';

/**
 * Build action list for StrategyBlock nodes
 *
 * Toolbar structure:
 * - Always visible: Edit, Delete, Generate Screens (first 3 icons)
 * - Dropdown menu: Duplicate, Open Screens, Set as Launch (only if NOT launch block)
 *
 * @param block - The strategy block entity
 * @param options.citesOldAnalysis - Spec D4 (U11): block screens cite an older
 *   insights analysis than the strategy's pin → offer the rebase regen
 * @returns Array of actions for toolbar/menu
 */
export function buildBlockActions(
  block: StrategyBlock,
  options?: { citesOldAnalysis?: boolean },
): NodeAction[] {
  const actions: NodeAction[] = [];

  // Always visible actions (first 3 - shown as icon buttons)
  actions.push({
    key: 'edit',
    title: 'Edit Block',
    icon: React.createElement(Edit),
    color: 'default',
  });

  actions.push({
    key: 'delete',
    title: 'Delete',
    icon: React.createElement(Delete),
    color: 'error',
  });

  // Generate screens with AI (shown for all blocks — handler checks if screens exist)
  actions.push({
    key: 'generate-screens',
    title: 'Generate Screens with AI',
    icon: React.createElement(AutoAwesome),
    color: 'primary',
    divider: true,
  });

  actions.push({
    key: 'copy',
    title: 'Duplicate Block',
    icon: React.createElement(ContentCopy),
    color: 'default',
  });

  // C7-2: rich server-side clone (screens + outgoing edges + provenance) with
  // an edge-policy popover. Sits with the copy family; Duplicate stays the
  // plain client-side copy (TC-008 contract).
  actions.push({
    key: 'clone-block',
    title: 'Clone Block…',
    icon: React.createElement(LibraryAdd),
    color: 'default',
  });

  // Block Examples Library: capture this block (screens + contract +
  // instructions) as a reusable project example.
  actions.push({
    key: 'save-as-example',
    title: 'Save as library blueprint…',
    icon: React.createElement(CollectionsBookmark),
    color: 'default',
  });

  // Open block screens in a new tab
  actions.push({
    key: 'open-in-tab',
    title: 'Open Screens in New Tab',
    icon: React.createElement(OpenInNew),
    color: 'default',
    divider: true,
  });

  // Dropdown menu actions (shown in More menu if >3 total actions)
  if (!block.isLaunchBlock) {
    // Only show "Set as Launch" for non-launch blocks
    actions.push({
      key: 'set-launch',
      title: 'Set as Launch',
      icon: React.createElement(PlayArrow),
      color: 'primary',
      divider: true,
    });
  }

  // Spec D4 (U11): a cherry-picked block citing an OLDER analysis than the
  // strategy's pin gets the rebase regen — keeps the block's structure and
  // creative approach, re-grounds the data on the current insights. Inserted
  // into the VISIBLE toolbar slots (first 3) per the confirmed design mock.
  if (options?.citesOldAnalysis) {
    actions.splice(2, 0, {
      key: 'rebase-block',
      title: 'Rebase block — re-ground on current insights',
      icon: React.createElement(Upgrade),
      color: 'warning',
      divider: true,
    });
  }

  return actions;
}

/**
 * Build action list for Screen nodes
 *
 * Toolbar structure:
 * - Always visible: Edit, Copy, Delete (first 3 icons)
 * - Dropdown menu: Set as Launch Screen (only if NOT launch screen)
 *
 * @param screen - The screen entity
 * @param isLaunchScreen - Whether this screen is the launch screen
 * @returns Array of actions for toolbar/menu
 */
export function buildScreenActions(_screen: unknown, isLaunchScreen?: boolean): NodeAction[] {
  const actions: NodeAction[] = [];

  // Always visible actions (first 3 - shown as icon buttons)
  actions.push({
    key: 'edit',
    title: 'Edit Screen',
    icon: React.createElement(Edit),
    color: 'default',
  });

  actions.push({
    key: 'copy',
    title: 'Duplicate',
    icon: React.createElement(ContentCopy),
    color: 'default',
  });

  actions.push({
    key: 'delete',
    title: 'Delete',
    icon: React.createElement(Delete),
    color: 'error',
  });

  // Dropdown menu actions (shown in More menu if >3 total actions)
  if (!isLaunchScreen) {
    // Only show "Set as Launch Screen" for non-launch screens
    actions.push({
      key: 'set-launch-screen',
      title: 'Set as Launch Screen',
      icon: React.createElement(PlayArrow),
      color: 'primary',
      divider: true,
    });
  }

  return actions;
}

/**
 * Future: Add custom actions based on additional conditions
 *
 * Examples:
 * - Permissions: if (!block.permissions?.canDelete) { action.disabled = true }
 * - Status: if (block.status === 'published') { add 'unpublish' action }
 * - Entity type: different actions for different blockTypes
 */
